import React, { useState } from 'react';
import {
  Archive, Bell, Buildings, CaretDown, CheckCircle, CircleNotch, Copy, Door, DownloadSimple, EnvelopeSimple, FilePdf,
  FolderSimplePlus, HandPalm, NotePencil, PaperPlaneTilt, PencilSimple, Prohibit, Trash, User, XCircle, type Icon,
} from '@phosphor-icons/react';
import {
  ANZEIGE_STATUS_LABEL, anzeigeStatus, bezugChips, fortschritt, signaturAktionen, unterzeichnerFarbe, type AnzeigeStatus, type SignaturAktion,
} from '../../../../lib/dokumente/signatur';
import { naechsterUnterzeichner } from '../../../../lib/signatur/vorgangAktionen';
import type { SignaturVorgang } from '../../../../lib/dokumente/signaturTypen';
import { deDatumZeit } from '../aktenschrank/dateiDarstellung';

const STATUS_STIL: Record<AnzeigeStatus, { fg: string; bg: string; icon: Icon }> = {
  entwurf: { fg: 'var(--ink2)', bg: 'var(--fill2)', icon: NotePencil },
  verschickt: { fg: 'var(--info)', bg: 'var(--info-soft)', icon: PaperPlaneTilt },
  abgeschlossen: { fg: 'var(--ok)', bg: 'var(--ok-soft)', icon: CheckCircle },
  ausgefuellt: { fg: 'var(--ok)', bg: 'var(--ok-soft)', icon: CheckCircle },
  abgebrochen: { fg: 'var(--bad)', bg: 'var(--bad-soft)', icon: XCircle },
};
const BEZUG_ICON = { objekt: Buildings, einheit: Door, mieter: User, eigentuemer: User } as const;
const U_STATUS = { offen: 'offen', angesehen: 'angesehen', signiert: 'signiert' } as const;

const AKTION: Record<SignaturAktion, { label: string; icon: Icon }> = {
  bearbeiten: { label: 'Bearbeiten', icon: PencilSimple },
  verschicken: { label: 'Verschicken', icon: PaperPlaneTilt },
  erinnern: { label: 'Erinnerung senden', icon: Bell },
  zwischenstand: { label: 'Zwischenstand', icon: FilePdf },
  vonHand: { label: 'Von Hand abgeschlossen', icon: HandPalm },
  abbrechen: { label: 'Abbrechen', icon: Prohibit },
  herunterladen: { label: 'Herunterladen', icon: DownloadSimple },
  email: { label: 'Per E-Mail senden', icon: EnvelopeSimple },
  ablegen: { label: 'In Akte ablegen', icon: FolderSimplePlus },
  kopieren: { label: 'Als neuen Entwurf kopieren', icon: Copy },
  loeschen: { label: 'Löschen', icon: Trash },
};
const HAUPT: readonly SignaturAktion[] = ['bearbeiten', 'erinnern', 'herunterladen', 'kopieren'];

/** Vorgangskarte (Handoff Dokumente §6.4, Screen 10). */
export function VorgangKarte({ v, bild, laeuft, onAktion }: {
  v: SignaturVorgang; bild: string | undefined; laeuft: boolean; onAktion: (a: SignaturAktion, v: SignaturVorgang) => void;
}) {
  const [offen, setOffen] = useState(false);
  const st = anzeigeStatus(v);
  const stil = STATUS_STIL[st];
  const f = fortschritt(v);
  const chips = bezugChips(v.bezug);
  const naechster = naechsterUnterzeichner(v.unterzeichner);
  const aktionen = signaturAktionen(v);

  return (
    <article className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 26, padding: 16, gap: 12 }} aria-label={v.titel}>
      <div className="flex items-start flex-wrap" style={{ gap: 12 }}>
        {bild ? (
          <img src={bild} alt="" className="shrink-0" style={{ width: 44, height: 56, borderRadius: 10, objectFit: 'cover', objectPosition: 'top', boxShadow: 'inset 0 0 0 1px var(--line)', background: '#fff' }} />
        ) : (
          <span className="flex items-center justify-center shrink-0" style={{ width: 44, height: 44, borderRadius: 14, background: stil.bg }}><stil.icon weight="fill" size={20} color={stil.fg} /></span>
        )}
        <div className="flex flex-col flex-1" style={{ gap: 2, minWidth: 180 }}>
          <span className="truncate" style={{ fontSize: 16, fontWeight: 800 }} title={v.titel}>{v.titel}</span>
          <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>
            Erstellt {deDatumZeit(v.created_at)} · {f.gesamt ? `${f.signiert} von ${f.gesamt} unterschrieben` : st === 'ausgefuellt' ? 'ohne Unterzeichner ausgefüllt' : 'keine Unterzeichner'}
          </span>
        </div>
        <div className="flex items-center" style={{ gap: 8, flex: '0 1 260px', minWidth: 140 }}>
          {f.gesamt > 0 && (
            <span className="flex flex-1" style={{ gap: 3, height: 6 }} aria-label={`${f.signiert} von ${f.gesamt} unterschrieben`}>
              {v.unterzeichner.map((u, i) => <span key={i} style={{ flex: 1, borderRadius: 3, background: u.status === 'signiert' ? 'var(--info)' : 'var(--fill3)' }} />)}
            </span>
          )}
          {v.vorlage_kategorie && <span className="truncate" style={{ height: 24, padding: '0 9px', borderRadius: 12, display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink2)', maxWidth: 140 }}>{v.vorlage_kategorie}</span>}
        </div>
      </div>

      <div className="flex items-center flex-wrap" style={{ gap: 6 }}>
        <span style={{ height: 28, padding: '0 11px', borderRadius: 14, display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 800, background: stil.bg, color: stil.fg }}>{ANZEIGE_STATUS_LABEL[st]}</span>
        {f.gesamt > 0 && (
          <button type="button" onClick={() => setOffen(o => !o)} aria-expanded={offen} aria-label={offen ? 'Unterzeichner zuklappen' : 'Unterzeichner aufklappen'}
            className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'transparent', color: 'var(--ink2)' }}>
            <CaretDown weight="bold" size={13} style={{ transform: offen ? 'rotate(180deg)' : undefined, transition: 'transform .2s' }} />
          </button>
        )}
        {v.abgelegt_am && <span className="flex items-center" style={{ gap: 5, fontSize: 12, fontWeight: 700, color: 'var(--ok)' }}><Archive weight="fill" size={13} />In Akte abgelegt · {deDatumZeit(v.abgelegt_am)}</span>}
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          {chips.map(c => {
            const I = BEZUG_ICON[c.art];
            return <span key={c.art} className="flex items-center" style={{ height: 28, padding: '0 10px', borderRadius: 14, gap: 5, fontSize: 12, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink2)', maxWidth: '100%' }}><I weight="fill" size={12} /><span className="truncate">{c.text}</span></span>;
          })}
        </div>
      )}

      {v.manuell_abgeschlossen_am && (
        <span className="immo-innen" style={{ borderRadius: 14, padding: '8px 12px', fontSize: 12, color: 'var(--ink2)' }}>
          Von Hand: am {deDatumZeit(v.manuell_abgeschlossen_am)} im Büro gegengezeichnet{v.manuell_notiz ? ` – ${v.manuell_notiz}` : ''}
        </span>
      )}

      {offen && (
        <ul className="immo-innen m-0 list-none flex flex-col" style={{ borderRadius: 18, padding: 8, gap: 2 }}>
          {v.unterzeichner.map((u, i) => {
            const dran = v.status === 'verschickt' && naechster?.index === i;
            return (
              <li key={i} className="flex items-center flex-wrap" style={{ gap: 10, padding: '6px 6px', minHeight: 44 }}>
                <span className="shrink-0" style={{ width: 10, height: 10, borderRadius: 5, background: unterzeichnerFarbe(i) }} />
                <span className="flex flex-col flex-1 min-w-0">
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{u.name || `Unterzeichner ${i + 1}`}</span>
                  <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{u.email || 'keine E-Mail'}</span>
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: u.status === 'signiert' ? 'var(--ok)' : u.status === 'angesehen' ? 'var(--info)' : 'var(--ink3)' }}>
                  {U_STATUS[u.status]}{u.status === 'signiert' && u.signiert_am ? ` · ${deDatumZeit(u.signiert_am)}` : ''}
                </span>
                {dran && aktionen.includes('erinnern') && (
                  <button type="button" onClick={() => onAktion('erinnern', v)} disabled={laeuft} className="immo-hover-flaeche cursor-pointer"
                    style={{ height: 44, padding: '0 12px', borderRadius: 14, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 12, fontWeight: 700 }}>Erneut senden</button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap" style={{ gap: 6 }}>
        {aktionen.map(a => {
          const A = AKTION[a];
          const haupt = HAUPT.includes(a) || (a === 'verschicken' && !aktionen.includes('bearbeiten'));
          return (
            <button key={a} type="button" onClick={() => onAktion(a, v)} disabled={laeuft}
              className={`${haupt ? '' : 'immo-hover-flaeche '}flex items-center cursor-pointer`}
              style={{ height: 44, padding: '0 14px', borderRadius: 14, border: 'none', gap: 7, fontSize: 13, fontWeight: 700, opacity: laeuft ? 0.55 : 1,
                ...(haupt ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' } : { background: 'var(--fill2)', color: a === 'loeschen' ? 'var(--bad)' : 'var(--ink)' }) }}>
              {laeuft && haupt ? <CircleNotch size={14} className="animate-spin" /> : <A.icon weight="bold" size={14} />}{A.label}
            </button>
          );
        })}
      </div>
    </article>
  );
}
